/**
 * 网页版上传限制（SSOT —— 唯一数据源，禁止在组件里再写死数字）
 *
 * 口径：单文件不超过 10M、最多 5 个。
 * 这个数字必须与两处保持一致，改动时一起改：
 *   1) 页面提示文案（HomePage.vue 的 tx-upload-tip）
 *   2) 后端 deploy/gateway 的 client_max_body_size（当前 100m，留有余量即可，
 *      真正的强制在下面的校验与后端 storage.web_max_upload_bytes）
 *
 * ⚠️ 小程序的上限是 50M（ceanx-mini/src/config/app.js 的 maxUploadBytes +
 *    后端 /api/upload 的 _read_upload_limited），与网页版**刻意不同**，
 *    因为两端上传接口不同（网页版走 /api/tasks、/api/tools/*；
 *    小程序走 /api/upload + /api/tools/invoke）。改这里不要动小程序。
 *
 * 为什么前端也要校验：后端虽然会拒，但用户要等整个文件传完才收到 413/400，
 * 大文件白等几十秒；前端在选中文件的那一刻就拦下来，体验更好。
 */

/** 单文件上限（字节）：10M */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/** 单次最多文件数 */
export const MAX_UPLOAD_COUNT = 5;

/** 上限的人类可读文案（报错里拼句子用，避免各处再写一遍 '10M'） */
export const MAX_UPLOAD_SIZE_TEXT = '10M';

/**
 * 字节数 → 人类可读（用于「你选的这个文件有 23.4M」这类提示）
 * @param {number} bytes
 * @returns {string}
 */
export function humanSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return n + 'B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + 'K';
  return (n / (1024 * 1024)).toFixed(1) + 'M';
}

/**
 * 校验单个文件大小。
 * @param {{size?: number, name?: string}} file
 * @returns {string|null} 出错时返回给用户看的中文提示，通过返回 null
 */
export function checkFileSize(file) {
  const size = (file && file.size) || 0;
  if (size > MAX_UPLOAD_BYTES) {
    const name = (file && file.name) || '该文件';
    return `「${name}」有 ${humanSize(size)}，超过单文件上限 ${MAX_UPLOAD_SIZE_TEXT}，请压缩后再上传`;
  }
  return null;
}

/**
 * 校验文件数量。
 * @param {number} count
 * @returns {string|null}
 */
export function checkFileCount(count) {
  if (count > MAX_UPLOAD_COUNT) {
    return `最多只能上传 ${MAX_UPLOAD_COUNT} 个文件，当前已选 ${count} 个`;
  }
  return null;
}

/**
 * 列表级校验：先查数量，再逐个查大小（保证只返回一条最该先看到的提示）。
 * @param {Array<{size?: number, name?: string}>} files
 * @returns {string|null}
 */
export function checkFileList(files) {
  const list = files || [];
  const countErr = checkFileCount(list.length);
  if (countErr) return countErr;
  for (const f of list) {
    const err = checkFileSize(f);
    if (err) return err;
  }
  return null;
}

/** 统一的提示文案，避免「最多 5 个」这类句子散落在各组件里 */
export const UPLOAD_LIMIT_TIP = `单文件不超过 ${MAX_UPLOAD_SIZE_TEXT} · 最多 ${MAX_UPLOAD_COUNT} 个`;
